import { useMemo, useState } from "react";
import { mergeClasses } from "@fluentui/react-components";
import { SectionChart } from "apps/ai-metrics/components/shared/SectionChart/SectionChart";
import { SectionHeader } from "apps/ai-metrics/components/shared/SectionHeader/SectionHeader";
import { StatCard } from "apps/ai-metrics/components/shared/StatCard/StatCard";
import { useSectionStyles } from "apps/ai-metrics/components/shared/useSectionStyles";
import {
  SECTION_NOTES,
  getMetricHelp,
  getChartHelp,
} from "apps/ai-metrics/config/metricHelpText";
import {
  ISprintCostRow,
  ISprintDeliveryCost,
  ISprintDeliveryRow,
} from "apps/ai-metrics/api/aiMetricsApi";
import {
  createSprintDeliveryOption,
  SERIES_COLORS,
  SPRINT_DELIVERY_COLORS,
} from "apps/ai-metrics/utils/echartOptions";
import { AiSpendSection } from "../AiSpendSection/AiSpendSection";
import { AdaptedVelocity } from "apps/ai-metrics/utils/dashboardAdapter";
import { useStyles } from "./SummarySection.styles";

const velocityFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 1,
});

const DELIVERY_CHART_TITLE = "Throughput & Velocity — Achieved per Sprint";
const DELIVERY_CHART_SUBTITLE =
  "Raw values · no baseline assumed · AI spend overlaid (right axis)";
const DELIVERY_CHART_SUBTITLE_BY_TEAM =
  "Raw values · one bar per team, throughput and velocity stacked · each team's AI spend overlaid (right axis)";
const THROUGHPUT_SERIES_NAME = "Throughput";
const VELOCITY_SERIES_NAME = "Velocity";
const SPEND_SERIES_NAME = "AI Spend";
const THROUGHPUT_UNIT = "items";
const VELOCITY_UNIT = "pts";

// The stack name is the team, so each team gets its own bar in a sprint and the
// two metrics pile up inside it. The velocity segment is faded because it sits
// on the same team colour as the throughput segment below it.
const VELOCITY_SEGMENT_OPACITY = 0.55;

// A row the sync stored without a team still has to land somewhere on the axis.
const UNASSIGNED_TEAM_LABEL = "Unassigned";

type SpendUnit = "tokens" | "dollar";
type DeliveryView = "combined" | "team";

const DELIVERY_VIEWS: Record<DeliveryView, { label: string }> = {
  combined: { label: "Combined" },
  team: { label: "By team" },
};

const DELIVERY_VIEW_ORDER: DeliveryView[] = ["combined", "team"];

const tokenAxisFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 2,
});

const dollarAxisFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  maximumFractionDigits: 2,
});

const SPEND_UNITS: Record<
  SpendUnit,
  { label: string; axisName: string; format: (value: number) => string }
> = {
  dollar: {
    label: "$ Dollar",
    axisName: "AI Spend ($)",
    format: (value) => dollarAxisFormatter.format(value),
  },
  tokens: {
    label: "Tokens",
    axisName: "Tokens",
    format: (value) => tokenAxisFormatter.format(value),
  },
};

const SPEND_UNIT_ORDER: SpendUnit[] = ["dollar", "tokens"];

const DELIVERY_CHART_LEGEND = [
  { name: THROUGHPUT_SERIES_NAME, color: SPRINT_DELIVERY_COLORS.throughput },
  { name: VELOCITY_SERIES_NAME, color: SPRINT_DELIVERY_COLORS.velocity },
  {
    name: SPEND_SERIES_NAME,
    color: SPRINT_DELIVERY_COLORS.spend,
    dashed: true,
  },
];

/**
 * Compact axis label for a sprint: "Team\\Sprint 12" reads as "S12".
 *
 * Ten or more sprints share the axis, so the full iteration path would either
 * overlap or be rotated to the point of being unreadable.
 */
const shortSprintLabel = (sprint: string): string => {
  const leaf = sprint.split(/[\\/]/).pop() ?? sprint;
  return leaf.replace(/^sprint\s+/i, "Sprint ");
};

const SPRINT_DAY_FORMAT: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
};

/**
 * The day part of a sprint boundary, read without crossing a timezone.
 *
 * The API sends BSON datetimes serialised in UTC, so a sprint starting at
 * midnight UTC would render as the previous day for anyone west of Greenwich if
 * the string were handed to `new Date` as-is. Only the `YYYY-MM-DD` prefix is
 * parsed, and as a local midnight, so the date shown is the date stored.
 */
const sprintDay = (value: string | null): string => {
  if (!value) return "";
  const day = new Date(`${value.slice(0, 10)}T00:00:00`);
  return Number.isNaN(day.getTime())
    ? ""
    : day.toLocaleDateString("en-GB", SPRINT_DAY_FORMAT);
};

/**
 * Second axis line for a sprint: "5 Jan – 18 Jan".
 *
 * A sprint number alone says nothing about when the work happened, which is the
 * question the spend line invites. One missing boundary still renders the other
 * rather than an em dash hanging off nothing.
 */
const sprintDateRange = (start: string | null, end: string | null): string => {
  const first = sprintDay(start);
  const last = sprintDay(end);
  if (first && last) return `${first} – ${last}`;
  return first || last;
};

const sprintAxisLabel = (row: ISprintDeliveryRow): string =>
  [
    shortSprintLabel(row.sprint),
    // sprintDateRange(row.sprint_start, row.sprint_end),
  ]
    .filter(Boolean)
    .join("\n");

const rowTeam = (row: ISprintDeliveryRow | ISprintCostRow): string =>
  row.team ?? UNASSIGNED_TEAM_LABEL;

// A sprint name is an iteration path, a team name is free text; the separator is
// a character neither can contain, so the composite key cannot collide.
const SPRINT_TEAM_SEPARATOR = "\u0000";

const sprintTeamKey = (sprint: string, team: string): string =>
  `${sprint}${SPRINT_TEAM_SEPARATOR}${team}`;

const rowKey = (row: ISprintDeliveryRow | ISprintCostRow): string =>
  sprintTeamKey(row.sprint, rowTeam(row));

const teamColor = (index: number): string =>
  SERIES_COLORS[index % SERIES_COLORS.length];

const teamSeriesName = (team: string, metric: string): string =>
  `${team} · ${metric}`;

/**
 * Teams in the order the API returned them, deduplicated.
 *
 * Fed both lists, because a team can appear in one and not the other - AI spend
 * is recorded per team member and a team can spend in a sprint it delivered
 * nothing in, which is exactly the comparison the chart is there to make.
 */
const teamsOf = (rows: (ISprintDeliveryRow | ISprintCostRow)[]): string[] =>
  Array.from(new Set(rows.map(rowTeam)));

/** The first row of each sprint, which carries the axis label for that sprint. */
const sprintsOf = (rows: ISprintDeliveryRow[]): ISprintDeliveryRow[] => {
  const seen = new Set<string>();
  return rows.filter((row) => {
    if (seen.has(row.sprint)) return false;
    seen.add(row.sprint);
    return true;
  });
};

interface IDeliveryChartInput {
  rows: ISprintDeliveryRow[];
  costRows: ISprintCostRow[];
  spendAxisName: string;
  formatSpend: (value: number) => string;
  readSpend: (row: ISprintCostRow) => number;
}

/**
 * One bar pair per sprint, the selected teams already summed by the API.
 *
 * Cost is index-aligned rather than looked up by sprint name: the API builds the
 * two lists from one pass over the same sprints, and a name is not guaranteed
 * unique across iteration paths whereas the position is.
 */
const createCombinedOption = ({
  rows,
  costRows,
  spendAxisName,
  formatSpend,
  readSpend,
}: IDeliveryChartInput) =>
  createSprintDeliveryOption({
    xLabels: rows.map(sprintAxisLabel),
    bars: [
      {
        name: THROUGHPUT_SERIES_NAME,
        data: rows.map((row) => row.throughput),
        color: SPRINT_DELIVERY_COLORS.throughput,
        unit: THROUGHPUT_UNIT,
      },
      {
        name: VELOCITY_SERIES_NAME,
        data: rows.map((row) => row.velocity),
        color: SPRINT_DELIVERY_COLORS.velocity,
        unit: VELOCITY_UNIT,
      },
    ],
    spend: [
      {
        name: SPEND_SERIES_NAME,
        data: rows.map((_, i) => {
          const costRow = costRows[i];
          return costRow ? readSpend(costRow) : null;
        }),
        color: SPRINT_DELIVERY_COLORS.spend,
      },
    ],
    spendAxisName,
    formatSpend,
  });

/**
 * The same sprints, but every team keeps its own bar and its own spend line.
 *
 * A team's throughput and velocity stack inside that one bar - stacking by team
 * rather than by metric is what keeps the teams visually separated, which is the
 * whole point of the view - and the teams then sit side by side per sprint.
 *
 * The axis stays one entry per sprint - a team that delivered nothing in a
 * sprint contributes no segment rather than shifting the others - so delivery
 * and cost are looked up by `(sprint, team)` instead of by position.
 */
const createSplitByTeamOption = ({
  rows,
  costRows,
  spendAxisName,
  formatSpend,
  readSpend,
}: IDeliveryChartInput) => {
  const sprints = sprintsOf(rows);
  const teams = teamsOf([...rows, ...costRows]);
  const deliveryByTeam = new Map(rows.map((row) => [rowKey(row), row]));
  const costByTeam = new Map(costRows.map((row) => [rowKey(row), row]));

  return createSprintDeliveryOption({
    xLabels: sprints.map(sprintAxisLabel),
    bars: teams.flatMap((team, i) => [
      {
        name: teamSeriesName(team, THROUGHPUT_SERIES_NAME),
        data: sprints.map(
          (sprint) =>
            deliveryByTeam.get(sprintTeamKey(sprint.sprint, team))
              ?.throughput ?? null,
        ),
        color: teamColor(i),
        unit: THROUGHPUT_UNIT,
        stack: team,
      },
      {
        name: teamSeriesName(team, VELOCITY_SERIES_NAME),
        data: sprints.map(
          (sprint) =>
            deliveryByTeam.get(sprintTeamKey(sprint.sprint, team))?.velocity ??
            null,
        ),
        color: teamColor(i),
        unit: VELOCITY_UNIT,
        stack: team,
        opacity: VELOCITY_SEGMENT_OPACITY,
      },
    ]),
    spend: teams.map((team, i) => ({
      name: teamSeriesName(team, SPEND_SERIES_NAME),
      data: sprints.map((sprint) => {
        const costRow = costByTeam.get(sprintTeamKey(sprint.sprint, team));
        return costRow ? readSpend(costRow) : null;
      }),
      color: teamColor(i),
    })),
    spendAxisName,
    formatSpend,
    showBarValueLabels: false,
  });
};

interface ISegmentedToggleProps<T extends string> {
  ariaLabel: string;
  options: T[];
  labelOf: (option: T) => string;
  value: T;
  onChange: (option: T) => void;
}

/**
 * The chart header's small pill toggle, shared by the unit and view switches.
 *
 * Two of them sit next to each other, so the markup - the divider between
 * segments, the pressed state, the group label - lives in one place.
 */
function SegmentedToggle<T extends string>({
  ariaLabel,
  options,
  labelOf,
  value,
  onChange,
}: ISegmentedToggleProps<T>): JSX.Element {
  const styles = useStyles();
  return (
    <div className={styles.unitGroup} role="group" aria-label={ariaLabel}>
      {options.map((option, i) => (
        <button
          key={option}
          type="button"
          className={mergeClasses(
            styles.unitButton,
            i < options.length - 1 ? styles.unitButtonDivider : undefined,
            value === option ? styles.unitButtonActive : undefined,
          )}
          aria-pressed={value === option}
          onClick={() => onChange(option)}
        >
          {labelOf(option)}
        </button>
      ))}
    </div>
  );
}

const percentChange = (
  current?: number,
  prior?: number,
): number | undefined => {
  if (current === undefined || prior === undefined || prior === 0) {
    return undefined;
  }
  return Math.round(((current - prior) / prior) * 100);
};

interface ISummarySectionProps {
  velocityKpis?: AdaptedVelocity;
  loading?: boolean;
  velocity?: number;
  velocityPrior?: number;
  velocityLoading?: boolean;
  velocityError?: boolean;
  comparisonEnabled: boolean;
  spendCost?: number;
  spendTokens?: number;
  spendPriorCost?: number;
  spendPriorTokens?: number;
  spendLoading?: boolean;
  spendError?: boolean;
  sprintDelivery?: ISprintDeliveryCost;
  sprintDeliveryLoading?: boolean;
  sprintDeliveryError?: boolean;
  /** True while `sprintDelivery` holds one row per sprint *and* team. */
  isSprintDeliverySplitByTeam?: boolean;
  onSprintDeliverySplitByTeamChange?: (isSplitByTeam: boolean) => void;
}

export const SummarySection = ({
  velocityKpis,
  loading,
  velocity,
  velocityPrior,
  velocityLoading = false,
  velocityError = false,
  comparisonEnabled,
  spendCost,
  spendTokens,
  spendPriorCost,
  spendPriorTokens,
  spendLoading,
  spendError,
  sprintDelivery,
  sprintDeliveryLoading = false,
  sprintDeliveryError = false,
  isSprintDeliverySplitByTeam = false,
  onSprintDeliverySplitByTeamChange,
}: ISummarySectionProps): JSX.Element => {
  const sectionStyles = useSectionStyles();
  const [spendUnit, setSpendUnit] = useState<SpendUnit>("tokens");

  const deliveryOption = useMemo(() => {
    const rows = sprintDelivery?.sprints ?? [];
    if (rows.length === 0) return undefined;

    const unit = SPEND_UNITS[spendUnit];
    const input: IDeliveryChartInput = {
      rows,
      costRows: sprintDelivery?.cost ?? [],
      spendAxisName: unit.axisName,
      formatSpend: unit.format,
      readSpend: (row) => (spendUnit === "tokens" ? row.tokens : row.cost_usd),
    };

    return isSprintDeliverySplitByTeam
      ? createSplitByTeamOption(input)
      : createCombinedOption(input);
  }, [sprintDelivery, spendUnit, isSprintDeliverySplitByTeam]);

  // In the per-team view the series carry team names, so the fixed
  // throughput/velocity/spend key no longer describes what is on screen.
  const deliveryLegend = useMemo(() => {
    if (!isSprintDeliverySplitByTeam) return DELIVERY_CHART_LEGEND;
    return teamsOf([
      ...(sprintDelivery?.sprints ?? []),
      ...(sprintDelivery?.cost ?? []),
    ]).map((team, i) => ({ name: team, color: teamColor(i) }));
  }, [isSprintDeliverySplitByTeam, sprintDelivery]);

  const velocityValue =
    velocity === undefined ? undefined : velocityFormatter.format(velocity);
  const velocityDelta = comparisonEnabled
    ? percentChange(velocity, velocityPrior)
    : undefined;

  return (
    <div className={sectionStyles.section}>
      <SectionHeader title="Summary" note={SECTION_NOTES.summary}>
        <div className={sectionStyles.cardsRow}>
          <StatCard
            label="THROUGHPUT"
            subtitle="Work items closed"
            higherIsBetter
            loading={loading}
            {...(!loading
              ? (velocityKpis?.throughput ?? { pending: true })
              : {})}
            info={getMetricHelp("THROUGHPUT")}
          />
          <StatCard
            label="VELOCITY"
            subtitle="Effort (story points) completed"
            value={velocityValue}
            delta={velocityDelta}
            higherIsBetter
            loading={velocityLoading}
            pending={
              !velocityLoading && !velocityError && velocity === undefined
            }
            info={getMetricHelp("VELOCITY")}
          />
          <StatCard
            label="COMPLETED WORK / 1-EFFORT ITEM"
            subtitle="Avg hours to complete one effort point"
            higherIsBetter={false}
            loading={loading}
            {...(!loading
              ? (velocityKpis?.workPerPerson ?? { pending: true })
              : {})}
            info={getMetricHelp("COMPLETED WORK / 1-EFFORT ITEM")}
          />
          <AiSpendSection
            cost={spendCost}
            tokens={spendTokens}
            priorCost={spendPriorCost}
            priorTokens={spendPriorTokens}
            comparisonEnabled={comparisonEnabled}
            loading={spendLoading}
            error={spendError}
          />
        </div>
        <SectionChart
          title={DELIVERY_CHART_TITLE}
          subtitle={
            isSprintDeliverySplitByTeam
              ? DELIVERY_CHART_SUBTITLE_BY_TEAM
              : DELIVERY_CHART_SUBTITLE
          }
          option={deliveryOption}
          legend={deliveryLegend}
          loading={sprintDeliveryLoading}
          emptyMessage={
            !sprintDeliveryLoading && !deliveryOption
              ? sprintDeliveryError
                ? "Failed to load data"
                : "No data for selected period"
              : undefined
          }
          info={getChartHelp(DELIVERY_CHART_TITLE)}
          actions={
            <>
              {onSprintDeliverySplitByTeamChange && (
                <SegmentedToggle
                  ariaLabel="Delivery grouping"
                  options={DELIVERY_VIEW_ORDER}
                  labelOf={(view) => DELIVERY_VIEWS[view].label}
                  value={isSprintDeliverySplitByTeam ? "team" : "combined"}
                  onChange={(view) =>
                    onSprintDeliverySplitByTeamChange(view === "team")
                  }
                />
              )}
              <SegmentedToggle
                ariaLabel="AI spend unit"
                options={SPEND_UNIT_ORDER}
                labelOf={(unit) => SPEND_UNITS[unit].label}
                value={spendUnit}
                onChange={setSpendUnit}
              />
            </>
          }
        />
      </SectionHeader>
    </div>
  );
};
